/**
 * Doctor Rx detail + edit-until-first-order (L3.4). Editable Rx allow a notes edit inline.
 * Rx digitised under the doctor's name are read-only for them (the API sends is_editable false).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { editPrescription, getDoctorPrescription } from "../../api/doctor";
import { ApiError, describeError } from "../../api/errors";
import { phoneLabel } from "../../lib/phone";
import { formatDose } from "../../lib/rxLines";
import { ScheduleBadge } from "../patient/Prescriptions";

const LIST = "/staff/doctor/prescriptions";

export function DoctorRxDetail() {
  const { id = "" } = useParams();
  // Back to the list as it was left (search, filters and sort live in its URL).
  const from = (useLocation().state as { back?: unknown } | null)?.back;
  const back = typeof from === "string" && from.startsWith(LIST) ? from : LIST;
  const qc = useQueryClient();
  const { data: rx, isLoading, isError } = useQuery({
    queryKey: ["doctor-prescription", id],
    queryFn: () => getDoctorPrescription(id),
  });
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (rx) setNotes(rx.notes ?? "");
  }, [rx]);

  const save = useMutation({
    mutationFn: () => editPrescription(id, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doctor-prescription", id] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    // A failed save says why (DOC-03); once the patient has ordered, the page re-reads the
    // prescription so it shows as locked.
    onError: (e) => {
      if (e instanceof ApiError && e.code === "RX_NOT_EDITABLE") {
        qc.invalidateQueries({ queryKey: ["doctor-prescription", id] });
      }
    },
  });
  const saveError =
    save.error instanceof ApiError && save.error.code === "RX_NOT_EDITABLE"
      ? "This prescription can no longer be edited: the patient has ordered it."
      : save.error
        ? describeError(save.error)
        : null;

  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (isError || !rx) return <p className="text-sm text-red-600">Prescription not found.</p>;

  return (
    <div className="max-w-2xl space-y-5">
      <Link to={back} className="text-sm text-brand hover:underline">
        ← Back
      </Link>

      <div className="rounded-xl border bg-white p-5 shadow-xs">
        <div className="flex items-center gap-2">
          <ScheduleBadge cls={rx.schedule_class} />
          {rx.is_editable ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Editable</span>
          ) : rx.is_digitized ? (
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-700">Digitised by pharmacy · read-only</span>
          ) : (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Locked (ordered or H1/X)</span>
          )}
        </div>
        <div className="mt-2 font-mono text-sm text-gray-700">{rx.prescription_id}</div>
        {rx.patient_name && (
          <div className="mt-1 text-sm">
            <span className="font-medium">{rx.patient_name}</span>
            <span className="text-gray-500">
              {rx.for_family_member_id
                ? rx.account_holder_name
                  ? ` · family member of ${rx.account_holder_name}`
                  : " · family member"
                : ""}
              {rx.patient_phone && ` · ${phoneLabel(rx.patient_phone)}`}
            </span>
          </div>
        )}
        <div className="mt-1 text-sm text-gray-500">
          Issued {new Date(rx.issued_at).toLocaleDateString()} · valid till {new Date(rx.expires_at).toLocaleDateString()}
        </div>
        {(rx.authored_by_name || rx.is_digitized) && (
          <div className="mt-1 text-sm text-gray-500">
            {rx.is_digitized ? (
              <>
                Digitised by {rx.authored_by_name ?? "a pharmacist"}
                {rx.on_behalf_of_doctor_name && <> on behalf of {rx.on_behalf_of_doctor_name}</>}
              </>
            ) : (
              <>Authored by {rx.authored_by_name}</>
            )}
          </div>
        )}

        <label className="mt-4 flex flex-col gap-1 text-sm">
          <span className="text-gray-500">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={!rx.is_editable}
            className="rounded-lg border px-3 py-1.5 disabled:bg-gray-50"
          />
        </label>
        {rx.is_editable && (
          <div className="mt-2 flex items-center gap-3">
            <button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
              {save.isPending ? "Saving…" : "Save notes"}
            </button>
            {saved && <span className="text-sm text-emerald-600">Saved</span>}
          </div>
        )}
        {saveError && <p className="mt-2 text-sm text-red-600">{saveError}</p>}
      </div>

      <div className="rounded-xl border bg-white shadow-xs">
        <div className="border-b px-5 py-3 text-sm font-medium text-gray-500">Medicines</div>
        <ul className="divide-y">
          {rx.lines.map((ln) => (
            <li key={ln.id} className="px-5 py-3">
              {/* A long name wraps on its own side; the quantity keeps its place and one line (RESP-12). */}
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 font-medium wrap-anywhere">{ln.medicine_name}</span>
                <span className="shrink-0 whitespace-nowrap text-sm text-gray-500">Qty {ln.quantity}</span>
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {formatDose(ln.dosage_value, ln.dosage_unit)} · {ln.food_relation.replace("_", " ")} · {ln.time_of_day.join(", ")}
                {ln.duration_days ? ` · ${ln.duration_days} days` : ""}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
